import React from 'react';

export interface SurveyResultsFilterValues {
  email: string;
  firstName: string;
  lastName: string;
  serial: string;
  platform: string;
  status: string;
  dateStart: string;
  dateEnd: string;
}

interface SurveyResultsFiltersProps extends SurveyResultsFilterValues {
  onEmail: (value: string) => void;
  onFirstName: (value: string) => void;
  onLastName: (value: string) => void;
  onSerial: (value: string) => void;
  onPlatform: (value: string) => void;
  onStatus: (value: string) => void;
  onDateStart: (value: string) => void;
  onDateEnd: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onClear: () => void;
}

export const emptySurveyResultsFilters = (): SurveyResultsFilterValues => ({
  email: '',
  firstName: '',
  lastName: '',
  serial: '',
  platform: '',
  status: '',
  dateStart: '',
  dateEnd: '',
});

export const surveyResultsFiltersFromSearch = (searchParams: URLSearchParams): SurveyResultsFilterValues => ({
  email: searchParams.get('email') || '',
  firstName: searchParams.get('firstName') || '',
  lastName: searchParams.get('lastName') || '',
  serial: searchParams.get('serial') || '',
  platform: searchParams.get('platform') || '',
  status: searchParams.get('status') || '',
  dateStart: searchParams.get('dateStart') || '',
  dateEnd: searchParams.get('dateEnd') || '',
});

export const surveyResultsFiltersToSearch = (values: SurveyResultsFilterValues): URLSearchParams => {
  const nextParams = new URLSearchParams();
  if (values.email.trim()) nextParams.set('email', values.email.trim());
  if (values.firstName.trim()) nextParams.set('firstName', values.firstName.trim());
  if (values.lastName.trim()) nextParams.set('lastName', values.lastName.trim());
  if (values.serial.trim()) nextParams.set('serial', values.serial.trim());
  if (values.platform) nextParams.set('platform', values.platform);
  if (values.status) nextParams.set('status', values.status);
  if (values.dateStart) nextParams.set('dateStart', values.dateStart);
  if (values.dateEnd) nextParams.set('dateEnd', values.dateEnd);
  return nextParams;
};

export const applySurveyResultsFilters = (
  params: Record<string, string | number>,
  searchParams: URLSearchParams
) => {
  const values = surveyResultsFiltersFromSearch(searchParams);
  if (values.email) params.email = values.email;
  if (values.firstName) params.firstName = values.firstName;
  if (values.lastName) params.lastName = values.lastName;
  if (values.serial) params.serial = values.serial;
  if (values.platform) params.platform = values.platform;
  if (values.status) params.status = values.status;
  if (values.dateStart) params.dateStart = values.dateStart;
  if (values.dateEnd) params.dateEnd = values.dateEnd;
};

const SurveyResultsFilters: React.FC<SurveyResultsFiltersProps> = ({
  email,
  firstName,
  lastName,
  serial,
  platform,
  status,
  dateStart,
  dateEnd,
  onEmail,
  onFirstName,
  onLastName,
  onSerial,
  onPlatform,
  onStatus,
  onDateStart,
  onDateEnd,
  onSubmit,
  onClear,
}) => (
  <div className="responsive-filter-bar d-flex flex-nowrap align-items-center gap-2 mb-3">
    <form onSubmit={onSubmit} className="d-flex flex-column gap-2 flex-grow-1 min-w-0">
      <div className="d-flex flex-nowrap align-items-center gap-2 w-100">
        <input
          type="text"
          className="form-control form-control-sm flex-shrink-0"
          placeholder="Email"
          value={email}
          onChange={(event) => onEmail(event.target.value)}
          style={{ width: '170px' }}
        />
        <input
          type="text"
          className="form-control form-control-sm flex-shrink-0"
          placeholder="First Name"
          value={firstName}
          onChange={(event) => onFirstName(event.target.value)}
          style={{ width: '120px' }}
        />
        <input
          type="text"
          className="form-control form-control-sm flex-shrink-0"
          placeholder="Last Name"
          value={lastName}
          onChange={(event) => onLastName(event.target.value)}
          style={{ width: '120px' }}
        />
        <input
          type="text"
          className="form-control form-control-sm flex-shrink-0"
          placeholder="Device Serial"
          value={serial}
          onChange={(event) => onSerial(event.target.value)}
          style={{ width: '140px' }}
        />
        <div className="input-group input-group-sm flex-shrink-0" style={{ width: 'auto' }}>
          <span className="input-group-text">Phone Platform</span>
          <select
            className="form-select"
            value={platform}
            onChange={(event) => onPlatform(event.target.value)}
            aria-label="Filter by phone platform"
            style={{ width: '120px' }}
          >
            <option value="">All</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>
        </div>
        <div className="input-group input-group-sm flex-shrink-0" style={{ width: 'auto' }}>
          <span className="input-group-text">Status</span>
          <select
            className="form-select"
            value={status}
            onChange={(event) => onStatus(event.target.value)}
            aria-label="Filter by status"
            style={{ width: '130px' }}
          >
            <option value="">All</option>
            <option value="completed">Completed</option>
            <option value="opted_out">Opted out</option>
          </select>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2 flex-shrink-0">
          <button type="button" className="btn btn-outline-secondary btn-sm flex-shrink-0" onClick={onClear}>
            Clear
          </button>
          <button type="submit" className="btn btn-primary btn-sm flex-shrink-0">Search</button>
        </div>
      </div>
      <div className="d-flex flex-nowrap align-items-center gap-2 w-100">
        <div className="input-group input-group-sm flex-shrink-0" style={{ width: 'auto' }}>
          <span className="input-group-text">Completed</span>
          <input
            type="datetime-local"
            className="form-control"
            value={dateStart}
            onChange={(event) => onDateStart(event.target.value)}
            aria-label="Completed after"
            title="Completed after"
            style={{ width: '210px' }}
          />
        </div>
        <input
          type="datetime-local"
          className="form-control form-control-sm flex-shrink-0"
          value={dateEnd}
          onChange={(event) => onDateEnd(event.target.value)}
          aria-label="Completed before"
          title="Completed before"
          style={{ width: '230px' }}
        />
      </div>
    </form>
  </div>
);

export default SurveyResultsFilters;
